'use client';
import { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  IconButton,
  Slide,
  alpha,
  useTheme,
  Collapse,
  Grid,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useSwipeable } from 'react-swipeable';
import { FullGalleryProject } from '../types';
import Titles from '../../common/Titles';
import DarkThemeProvider from '../../../../../providers/DarkThemeProvider';
import OptionalViewport from '../../../../OptionalViewport';
import Arrow from './Arrow';
import SwitchImage from '../../../../gallery/SwitchImage';
import useIsMobile from '../../../../../utils/useIsMobile';
import RichText from '../../../../RichText';

type Props = {
  open: boolean;
  onClose: () => void;
  projects: FullGalleryProject[];
  selectedIndex: number;
  onNext: () => void;
  onPrev: () => void;
};

const FullScreenView = ({
  open,
  onClose,
  projects,
  selectedIndex,
  onNext,
  onPrev,
}: Props) => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const [showDetails, setShowDetails] = useState(false);
  const activeImage = projects[selectedIndex];
  const [transition, setTransition] = useState(0);

  useEffect(() => {
    if (!isMobile) {
      setShowDetails(true);
    }
  }, [isMobile]);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      onNext();
      setTransition(0);
    },
    onSwipedRight: () => {
      onPrev();
      setTransition(0);
    },
    onSwipedUp: () => setShowDetails(true),
    onSwipedDown: () => {
      if (!showDetails) onClose();
      setTransition(0);
      setShowDetails(false);
    },
    onSwiping: (eventData) => {
      if (['Left', 'Right'].includes(eventData.dir)) {
        setTransition(eventData.deltaX);
      }
    },
    preventScrollOnSwipe: true,
  });

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') onPrev();
    if (event.key === 'ArrowRight') onNext();
  };

  return (
    <DarkThemeProvider>
      <Modal
        open={open}
        onClose={() => {
          onClose();
          setTransition(0);
        }}
        closeAfterTransition
        onKeyDown={handleKeyDown as any}
      >
        <Slide in={open} direction={'up'}>
          <Box
            {...handlers}
            sx={{
              position: 'relative',
              width: '100vw',
              height: '100dvh',
              outline: 'none',
              bgcolor: 'background.default',
              py: { xs: 0, md: 8 },
              display: isMobile ? 'block' : 'flex',
              flexDirection: 'column',
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                backgroundColor: alpha(theme.palette.common.black, 0.6),
                border: !isMobile ? '1px solid' : undefined,
                position: 'absolute',
                top: 16,
                right: 16,
                color: 'white',
                zIndex: 10,
              }}
            >
              <Close />
            </IconButton>

            <OptionalViewport viewport={'desktop'}>
              <Box
                sx={{
                  zIndex: 10,
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  pointerEvents: 'none',
                }}
              >
                <Arrow
                  onClick={onPrev}
                  position={'left'}
                  sx={{ pointerEvents: 'auto' }}
                />
                <Arrow
                  onClick={onNext}
                  position={'right'}
                  sx={{ pointerEvents: 'auto' }}
                />
              </Box>
            </OptionalViewport>

            <Box
              sx={{
                display: 'flex',
                height: '100%',
                width: '100%',
                transform: `translateX(-${selectedIndex * 100}%) translateX(${transition}px)`,
                transition: !transition
                  ? 'transform 0.4s cubic-bezier(0.25, 0.8, 0.5, 1)'
                  : 'none',
                willChange: 'transform',
              }}
            >
              {projects.map((project, index) => {
                const renderNeighbours =
                  Math.abs(selectedIndex - index) <= 1 ||
                  (selectedIndex === 0 && index === projects.length - 1) ||
                  (selectedIndex === projects.length - 1 && index === 0);

                return (
                  <Box
                    key={project.id}
                    sx={{
                      width: '100%',
                      height: '100%',
                      flexShrink: 0,
                      px: { xs: 0, md: 8 },
                    }}
                  >
                    {renderNeighbours ? (
                      <Box
                        sx={{
                          height: '100%',
                          borderRadius: isMobile ? 0 : 1,
                          bgcolor: 'background.paper',
                          px: { sx: 1, md: 4 },
                          py: { xs: 0, md: 4 },
                        }}
                      >
                        <SwitchImage
                          beforeImage={project.beforeImage}
                          afterImage={project.image}
                          sx={{
                            height: '100%',
                            borderRadius: 'inherit',
                            bgcolor: 'background.paper',
                            overflow: 'unset',
                          }}
                          imgStyle={{
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </Box>

            <Grid
              container
              sx={{
                position: isMobile ? 'absolute' : 'relative',
                bottom: 0,
                left: 0,
                width: '100%',
                bgcolor: 'background.default',
                p: { xs: 1, md: 4 },
              }}
            >
              <Grid size={{ xs: 12, md: 6 }}>
                <Titles
                  title={activeImage?.title}
                  subtitle={activeImage?.subtitle}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Collapse in={showDetails}>
                  <RichText
                    sx={{ mt: { xs: 4, md: 0 }, color: 'text.secondary' }}
                  >
                    {activeImage?.description}
                  </RichText>
                </Collapse>
              </Grid>
            </Grid>
          </Box>
        </Slide>
      </Modal>
    </DarkThemeProvider>
  );
};

export default FullScreenView;
